import { PortalMap, PortalType } from "@studyportals/domain-client";
import { getIsProduction } from "@utils/environment";

const reportRollbarError = (message: string, error: Error): void => {
	if (!import.meta.server) return;

	void import("../../infrastructure/rollbar/RollbarService.server").then(({ getServerRollbar }) => {
		const rollbar = getServerRollbar();
		if (rollbar) {
			rollbar.error(message, error);
		}
	});
};

export class PortalMapFactory {
	private defaultPortalMap: PortalMap;

	constructor() {
		if (getIsProduction()) {
			this.defaultPortalMap = this.createProductionPortalMap();
		} else {
			this.defaultPortalMap = this.createTestingPortalMap();
		}
	}

	public buildMap(data?: Record<PortalType, string> | string): PortalMap {
		if (getIsProduction()) return this.defaultPortalMap;

		try {
			if (typeof data === 'string') {
				const parsed = JSON.parse(data);
				const entries = Object.entries(parsed as Record<PortalType, string>);
				return entries.length > 0 ? new Map(entries) as PortalMap : this.defaultPortalMap;
			}

			if (typeof data === 'object') {
				const entries = Object.entries(data);
				return entries.length > 0 ? new Map(entries) as PortalMap : this.defaultPortalMap;
			}
		} catch (e) {
			reportRollbarError('Failed to parse PortalMap string', e as Error);
			console.error('Failed to parse PortalMap string', e);
		}

		return this.defaultPortalMap;
	}

	private createProductionPortalMap(): Map<PortalType, string> {
		return new Map<PortalType, string>()
			.set(PortalType.MASTER, 'https://www.mastersportal.com')
			.set(PortalType.BACHELOR, 'https://www.bachelorsportal.com')
			.set(PortalType.PHD, 'https://www.phdportal.com')
			.set(PortalType.SHORT, 'https://www.shortcoursesportal.com')
			.set(PortalType.DISTANCE_LEARNING, 'https://www.distancelearningportal.com')
	}

	private createTestingPortalMap(): Map<PortalType, string> {
		return new Map<PortalType, string>()
			.set(PortalType.MASTER, 'https://playground.mastersportal.fyi')
			.set(PortalType.BACHELOR, 'https://playground.bachelorsportal.fyi')
			.set(PortalType.PHD, 'https://playground.phdportal.fyi')
			.set(PortalType.SHORT, 'https://playground.shortcoursesportal.fyi')
			.set(PortalType.DISTANCE_LEARNING, 'https://playground.distancelearningportal.fyi')
	}
}
